import React, { useEffect, useMemo } from "react"
import {
  MRT_ColumnDef,
  MRT_TableOptions,
  MaterialReactTable,
} from "material-react-table"

import { useDispatch, useSelector } from "react-redux"
import {
  setMetricGoalsTableData,
  setSelectedMetricGoalsTab,
} from "../../../redux/slices/metricSlice"
import { setLoading } from "../../../redux/slices/appSlice"

import { getCurrentYear } from "../../../../helper/utils"
import { MetricGoalsMonths, permissions } from "../../../../config/constants"
import GoalsMetricNameCell from "../Cell/GoalsMetricNameCell"
import GoalsDropdownCell from "../Cell/GoalsDropdownCell"
import GoalsCell from "../Cell/GoalsCell"
import { NoBucketWrapper, TableWrapper } from "../Atom"
import GoalsHeader from "../Header/GoalsHeader"
import { convertToFloat, convertToInt, getLevelSum } from "./utils"
import { fixNumber } from "../../../pages/MetricGoal/utils"
import { colors } from "../../../../styles/commonStyles"
import GoalsCellColored from "../Cell/GoalsCellColored"
import { CircularProgress } from "@mui/material"

const MetricGoalsTable = (props: any) => {
  const { updateYearGoal, updateMonthGoal, fetchGoalsByBucket } = props

  const loading = useSelector((state: any) => state.app.loading)

  const allPerms = useSelector((state: any) => {
    return state.rolesAndPerms.userPerms
  })

  const goalsData = useSelector((state: any) => {
    return state.metric.metricGoalsData
  })

  const goalsTableData = useSelector((state: any) => {
    return state.metric.metricGoalsTableData
  })

  const maevaSelectedGroups = useSelector((state: any) => {
    return state.metric.maevaSelectedGroups
  })

  const maevaSelectedRegions = useSelector((state: any) => {
    return state.metric.maevaSelectedRegions
  })

  const maevaSelectedPractices = useSelector((state: any) => {
    return state.metric.maevaSelectedPractices
  })

  const selectedGroupFilter = maevaSelectedGroups[0]

  const selectedRegionFilter = maevaSelectedRegions[0]

  const selectedPracticeFilter = maevaSelectedPractices[0]

  const selectedDateFilter = useSelector((state: any) => {
    return state.metric.metricGoalsSelectedYear
  })

  const metricGoalsTabs = useSelector((state: any) => {
    return state.metric.metricGoalsTabs
  })

  const selectedMetricGoalsTab = useSelector((state: any) => {
    return state.metric.selectedMetricGoalsTab
  })

  const dispatch = useDispatch()

  const currentYear = getCurrentYear()

  /* 
   * Helper function to recursively find and update the goal in the nested structure 
   * selectedMetricGoalsTab -> metrics -> group -> regions -> practices
   */
  const updateLocalState = (
    metricId: string,
    groupSk: string,
    regionKey: string | null,
    practiceSk: string | null,
    monthKey: string | null, // null means update all months (for apply to row / clear row)
    newValue: any,
  ) => {
    // Deep clone the state to avoid mutation errors
    const newData = JSON.parse(JSON.stringify(selectedMetricGoalsTab))

    if (newData && newData.metrics) {
      // Find the target metric
      const metric = newData.metrics.find((m: any) => m.metric_id === metricId)

      if (metric) {
        // Identify the target object (Group, Region, or Practice)
        let targetObject: any = null

        // If practice level
        if (practiceSk && regionKey) {
          const region = metric.group?.regions?.find(
            (r: any) => r.region === regionKey,
          )
          if (region) {
            targetObject = region.practices?.find(
              (p: any) => p.practice_sk == practiceSk,
            )
          }
        }
        // If region level
        else if (regionKey) {
          // Check if regionKey matches the metric's group region if depth is not explicit
          targetObject = metric.group?.regions?.find(
            (r: any) => r.region === regionKey,
          )
        }
        // If group level (metric)
        else {
          // For row depth 0 (group), the data is typically on the group object itself or aggregated.
          // However based on populateGoalsTable, 'metric' itself holds 'group' data.
          // But 'metric.group' is where regions are. 
          // If update is on group row, we might be updating 'metric.group.goals' or similar.
          // Looking at populateGoalsTable line 283: tempObj = { ...metric, ... }
          // So check if metric.group has goals.
          targetObject = metric.group
        }

        if (targetObject) {
          if (!targetObject.goals) {
            targetObject.goals = {}
          }

          if (monthKey) {
            // Update specific month
            if (!targetObject.goals[monthKey]) {
              targetObject.goals[monthKey] = {}
            }
            targetObject.goals[monthKey].goal_value = newValue
          } else {
            // Update ALL months (Apply/Clear Row)
            Object.keys(MetricGoalsMonths).forEach((mKey) => {
              if (!targetObject.goals[mKey]) {
                targetObject.goals[mKey] = {}
              }
              targetObject.goals[mKey].goal_value = newValue
            })
          }
        }
      }
    }

    // Dispatch the updated data to redux
    dispatch(setSelectedMetricGoalsTab(newData))
  }


  const handleApplyToRow = async (row: any, valueToApply: any) => {

    const cohercedVal =
      row?.original?.goal_type === "%"
        ? convertToFloat(valueToApply) / 100
        : valueToApply.replaceAll("$", "")
    // : convertToFloat(valueToApply)

    const response: any = await updateYearGoal({
      metric_year: selectedDateFilter?.label || currentYear,
      group_sk: row?.original?.group_sk,
      region: row?.depth != 0 ? row?.original?.region : null,
      practice_sk: row?.depth == 2 ? row?.original?.practice_sk : null,
      metric_id: row?.original?.metric_id,
      goal_value: cohercedVal,
    }, false)

    if (response?.statusCode === 200) {
      updateLocalState(
        row?.original?.metric_id,
        row?.original?.group_sk,
        row?.depth != 0 ? row?.original?.region : null,
        row?.depth == 2 ? row?.original?.practice_sk : null,
        null, // Update all months
        // If it's a percentage, use the raw value (or multiplied back) for display consistency if needed
        // The user says "to show we again convert them multiplying by 100", implying we should store the '100x' value locally?
        // Actually, if cohercedVal is 0.5, and we store 0.5.
        // User says "it is dividing... but to show we again convert them...".
        // If we store 0.5, and populateGoalsTable uses fixNumber which expects decimal for %, it should be fine.
        // BUT user complaint suggests 0.5 shows as 0.5 not 50.
        // So we pass valueToApply (which is already 50 for %) to local state if it's %.
        row?.original?.goal_type === "%" ? parseFloat(valueToApply) : cohercedVal
      )
    }
  }

  const handleClearRow = async (row: any) => {
    const response: any = await updateYearGoal({
      metric_year: selectedDateFilter?.label || currentYear,
      group_sk: row?.original?.group_sk,
      region: row?.depth != 0 ? row?.original?.region : null,
      practice_sk: row?.depth == 2 ? row?.original?.practice_sk : null,
      metric_id: row?.original?.metric_id,
      goal_value: null,
    }, false)

    if (response?.statusCode === 200) {
      updateLocalState(
        row?.original?.metric_id,
        row?.original?.group_sk,
        row?.depth != 0 ? row?.original?.region : null,
        row?.depth == 2 ? row?.original?.practice_sk : null,
        null, // Clear all months
        null
      )
    }
  }

  const populateGoalsTable = () => {
    let temp: any = []

    if (!selectedMetricGoalsTab) {
      setSelectedMetricGoalsTab({ ...metricGoalsTabs[0], index: 0 })
    }

    selectedMetricGoalsTab?.metrics?.forEach((metric: any) => {
      let tempRegionsArr: any = []

      let tempObj: any = {}
      const parseFun = metric?.goal_type === "#" ? parseInt : parseFloat

      // setting data for practices subrows
      metric?.group?.regions?.forEach((region: any) => {
        let tempPracticesArray: any = []
        region?.practices.forEach((practice: any) => {
          // @todo -defer
          //let goalsCount = 0

          // let year_total = Object.keys(practice?.goals)?.reduce(
          //   (total: number, key: string) => {
          //     const goal_value = practice?.goals?.[key]?.goal_value
          //     total += parseFun(goal_value || "0")
          //     goalsCount += goal_value ? 1 : 0
          //     return total
          //   },
          //   0.0,
          // )

          // if (metric?.goal_type === "%") {
          //   year_total = year_total / goalsCount
          // }

          tempPracticesArray.push({
            ...practice,
            metric_name: practice.name,
            group_sk: metric?.group?.group_sk,
            metric_id: metric?.metric_id,
            goal_type: metric?.goal_type,
            trend_type: metric?.trend_type,
            baseline_value: practice?.baseline_value
              ? fixNumber(
                practice?.baseline_value,
                metric?.goal_type,
                metric?.metric_precision,
              )
              : "Not Available",
            metric_benchmark: metric?.metric_benchmark
              ? fixNumber(
                metric?.metric_benchmark,
                metric?.goal_type,
                metric?.metric_precision,
              )
              : "Not Available",
            trend_value: practice?.trend_value
              ? fixNumber(
                practice?.trend_value,
                metric?.trend_type,
                metric?.metric_precision,
              )
              : "Not Available",
            // year_total, @todo -defer
            jan: fixNumber(
              practice?.goals?.jan?.goal_value,
              metric?.goal_type,
              metric?.metric_precision,
            ),
            feb: fixNumber(
              practice?.goals?.feb?.goal_value,
              metric?.goal_type,
              metric?.metric_precision,
            ),
            mar: fixNumber(
              practice?.goals?.mar?.goal_value,
              metric?.goal_type,
              metric?.metric_precision,
            ),
            apr: fixNumber(
              practice?.goals?.apr?.goal_value,
              metric?.goal_type,
              metric?.metric_precision,
            ),
            may: fixNumber(
              practice?.goals?.may?.goal_value,
              metric?.goal_type,
              metric?.metric_precision,
            ),
            june: fixNumber(
              practice?.goals?.june?.goal_value,
              metric?.goal_type,
              metric?.metric_precision,
            ),
            july: fixNumber(
              practice?.goals?.july?.goal_value,
              metric?.goal_type,
              metric?.metric_precision,
            ),
            aug: fixNumber(
              practice?.goals?.aug?.goal_value,
              metric?.goal_type,
              metric?.metric_precision,
            ),
            sep: fixNumber(
              practice?.goals?.sep?.goal_value,
              metric?.goal_type,
              metric?.metric_precision,
            ),
            oct: fixNumber(
              practice?.goals?.oct?.goal_value,
              metric?.goal_type,
              metric?.metric_precision,
            ),
            nov: fixNumber(
              practice?.goals?.nov?.goal_value,
              metric?.goal_type,
              metric?.metric_precision,
            ),
            dec: fixNumber(
              practice?.goals?.dec?.goal_value,
              metric?.goal_type,
              metric?.metric_precision,
            ),
          })
        })

        if (
          selectedPracticeFilter &&
          selectedPracticeFilter?.label?.toLowerCase() != "all"
        ) {
          tempPracticesArray = tempPracticesArray.filter(
            (tempPractice: any) =>
              tempPractice?.practice_sk == selectedPracticeFilter?.practice_sk,
          )
        }

        // setting regions subrows
        tempRegionsArr.push({
          ...region,
          subRows: [...tempPracticesArray],
          metric_name: region?.region,
          group_sk: metric?.group?.group_sk,
          metric_id: metric?.metric_id,
          goal_type: metric?.goal_type,
          trend_type: metric?.trend_type,
          ...getLevelSum({
            levelValues: tempPracticesArray,
            aggType: metric?.goal_type,
            trendAggType: metric?.trend_type,
            bucket: selectedMetricGoalsTab?.metric_bucket,
            precision: metric?.metric_precision,
            fallbackData: {
              data: region,
              metric,
            },
          }),
        })
      })

      if (selectedRegionFilter && selectedRegionFilter?.region != "all") {
        tempRegionsArr = tempRegionsArr.filter(
          (tempRegion: any) =>
            tempRegion?.region.toLowerCase() == selectedRegionFilter?.region,
        )
      }

      // setting group rows
      tempObj = {
        ...metric,
        subRows: [...tempRegionsArr],
        group_sk: metric?.group?.group_sk,
        trend_highlighting: metric?.group?.trend_highlighting,
        ...getLevelSum({
          levelValues: tempRegionsArr,
          aggType: metric?.goal_type,
          precision: metric?.metric_precision,
          bucket: selectedMetricGoalsTab?.metric_bucket,
          fallbackData: {
            data: metric?.group,
            metric,
          },
        }),
      }

      temp.push(tempObj)
    })

    dispatch(setMetricGoalsTableData(temp))
  }

  const handleCellValueChange = async (
    cell: any,
    row: any,
    valueToApply: any,
  ) => {
    if (valueToApply == "") {
      return
    }
    // Don't show global loader for cell edit to prevent UI flicker/reset
    // dispatch(setLoading(true)) 

    const cohercedVal =
      row?.original?.goal_type === "%"
        ? parseFloat(valueToApply) / 100
        : valueToApply

    const monthKey = cell?.id.split("_")[1] // e.g. "jan", "feb"

    const response: any = await updateMonthGoal({
      metric_year: selectedDateFilter?.label || currentYear,
      group_sk: row?.original?.group_sk,
      region: row?.depth != 0 ? row?.original?.region : null,
      practice_sk: row?.depth == 2 ? row?.original?.practice_sk : null,
      metric_id: row?.original?.metric_id,
      goal_value: cohercedVal,
      metric_month: MetricGoalsMonths[monthKey],
    }, false)

    if (response?.statusCode === 200) {
      updateLocalState(
        row?.original?.metric_id,
        row?.original?.group_sk,
        row?.depth != 0 ? row?.original?.region : null,
        row?.depth == 2 ? row?.original?.practice_sk : null,
        monthKey,
        row?.original?.goal_type === "%" ? parseFloat(valueToApply) : cohercedVal
      )
    } else {
      // If error, maybe toast? The API wrapper usuall handles toasts or we can add one here.
      // Reverting the cell value might be complex without a controlled input state, 
      // but populating table again might fix it. 
      // For now, reliance on API wrapper toast.
    }
    // dispatch(setLoading(false))
  }

  useEffect(() => {
    populateGoalsTable()
  }, [
    goalsData,
    maevaSelectedGroups,
    maevaSelectedRegions,
    maevaSelectedPractices,
    selectedMetricGoalsTab,
  ])

  const enableSelectiveEditing = React.useCallback(
    (row: any) => {
      if (!allPerms.includes(permissions.UPDATE_MONTH_GOALS)) {
        return false
      }

      const excludedMetrics = [
        "Net Production vs Goal",
        "Net Production vs Forecast",
        "Collections $",
      ]
      if (selectedMetricGoalsTab?.metric_bucket !== "Production") {
        return true
      }
      let metricName = ""
      if (row?.depth === 2) {
        return true
      } else if (row?.depth === 1) {
        const parent = row?.getParentRow()
        metricName = parent?.original?.metric_name
      } else {
        metricName = row?.original?.metric_name
      }

      return !excludedMetrics.includes(metricName)
    },
    [selectedMetricGoalsTab],
  )

  const enableSelectiveYearEditing = React.useCallback(
    (row: any) => {
      if (!allPerms.includes(permissions.UPDATE_YEAR_GOALS)) {
        return false
      }

      const excludedMetrics = [
        "Net Production vs Goal",
        "Net Production vs Forecast",
        "Collections $",
      ]
      if (selectedMetricGoalsTab?.metric_bucket !== "Production") {
        return true
      }
      let metricName = ""
      if (row?.depth === 2) {
        return true
      } else if (row?.depth === 1) {
        const parent = row?.getParentRow()
        metricName = parent?.original?.metric_name
      } else {
        metricName = row?.original?.metric_name
      }

      return !excludedMetrics.includes(metricName)
    },
    [selectedMetricGoalsTab],
  )

  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        header: "Metric Name",
        accessorKey: "metric_name",
        Header: ({ row }: any) => (
          <GoalsHeader headerName={"Metric Name"} width={`300px`} />
        ),
        Cell: ({ row, renderedCellValue }: any) => (
          <GoalsMetricNameCell
            row={row}
            renderedCellValue={renderedCellValue}
          />
        ),
        size: 300,
        enableEditing: false,
        enablePinning: true,
        muiTableBodyCellProps: () => ({
          sx: {
            border: "1px solid #E4E4E4",
            padding: "0 !important",
            "&:hover": {
              border: "1px solid #E4E4E4",
              transition: "none",
            },
          },
        }),
      },
      {
        accessorKey: "baseline_value",
        header: "Baseline",
        Header: () => <GoalsHeader headerName={"Baseline"} width={`200px`} />,
        Cell: ({ row, renderedCellValue }: any) =>
          !enableSelectiveYearEditing(row) ? (
            <GoalsCell row={row} renderedCellValue={renderedCellValue} />
          ) : (
            <GoalsDropdownCell
              row={row}
              renderedCellValue={renderedCellValue}
              handleApplyToRow={handleApplyToRow}
              handleClearRow={handleClearRow}
            />
          ),
        size: 200,
        enableEditing: false,
        enablePinning: true,
        muiTableBodyCellProps: () => ({
          sx: {
            border: "1px solid #E4E4E4",
            padding: "0 !important",
            "&:hover": {
              border: "1px solid #E4E4E4",
              transition: "none",
            },
          },
        }),
      },
      {
        accessorKey: "metric_benchmark",
        header: "Benchmark",
        Header: ({ row }: any) => (
          <GoalsHeader headerName={"Benchmark"} width={`200px`} />
        ),
        Cell: ({ row, renderedCellValue }: any) =>
          !enableSelectiveYearEditing(row) ? (
            <GoalsCell row={row} renderedCellValue={renderedCellValue} />
          ) : (
            <GoalsDropdownCell
              row={row}
              renderedCellValue={renderedCellValue}
              handleApplyToRow={handleApplyToRow}
              handleClearRow={handleClearRow}
            />
          ),
        size: 200,
        enableEditing: false,
        enablePinning: true,
        muiTableBodyCellProps: () => ({
          sx: {
            border: "1px solid #E4E4E4",
            padding: "0 !important",
            "&:hover": {
              border: "1px solid #E4E4E4",
              transition: "none",
            },
          },
        }),
      },
      {
        accessorKey: "trend_value",
        header: "Current Trend",
        Header: ({ row }: any) => (
          <GoalsHeader headerName={"Current Trend"} width={`200px`} />
        ),
        Cell: ({ row, renderedCellValue }: any) => (
          <GoalsCellColored row={row} renderedCellValue={renderedCellValue} />
        ),
        size: 200,
        enableEditing: false,
        enablePinning: true,
        muiTableBodyCellProps: () => ({
          sx: {
            border: "1px solid #E4E4E4",
            padding: "0 !important",
            "&:hover": {
              border: "1px solid #E4E4E4",
              transition: "none",
            },
          },
        }),
      },
      // @todo -defer
      // {
      //   accessorKey: "year_total",
      //   header: "Yearly Goals",
      //   enableEditing: false,
      //   muiTableBodyCellProps: {
      //     sx: {
      //       backgroundColor: colors.grey50,
      //     },
      //   },
      //   Header: () => (
      //     <GoalsHeader headerName={"Yearly Goals"} width={`150px`} />
      //   ),
      //   size: 150,
      //   Cell: ({ row, renderedCellValue }: any) => (
      //     <GoalsCell row={row} renderedCellValue={renderedCellValue} />
      //   ),
      // },
      {
        accessorKey: "jan",
        header: `Jan - ${selectedDateFilter?.label || currentYear}`,
        Header: ({ row }: any) => (
          <GoalsHeader
            headerName={`Jan - ${selectedDateFilter?.label || currentYear}`}
            width={`150px`}
          />
        ),
        size: 150,
        Cell: ({ row, renderedCellValue }: any) => (
          <GoalsCell row={row} renderedCellValue={renderedCellValue} />
        ),
      },
      {
        accessorKey: "feb",
        header: `Feb - ${selectedDateFilter?.label || currentYear}`,
        Header: ({ row }: any) => (
          <GoalsHeader
            headerName={`Feb - ${selectedDateFilter?.label || currentYear}`}
            width={`150px`}
          />
        ),
        size: 150,
        Cell: ({ row, renderedCellValue }: any) => (
          <GoalsCell row={row} renderedCellValue={renderedCellValue} />
        ),
      },
      {
        accessorKey: "mar",
        header: `Mar - ${selectedDateFilter?.label || currentYear}`,
        Header: ({ row }: any) => (
          <GoalsHeader
            headerName={`Mar - ${selectedDateFilter?.label || currentYear}`}
            width={`150px`}
          />
        ),
        size: 150,
        Cell: ({ row, renderedCellValue }: any) => (
          <GoalsCell row={row} renderedCellValue={renderedCellValue} />
        ),
      },
      {
        accessorKey: "apr",
        header: `Apr - ${selectedDateFilter?.label || currentYear}`,
        Header: ({ row }: any) => (
          <GoalsHeader
            headerName={`Apr - ${selectedDateFilter?.label || currentYear}`}
            width={`150px`}
          />
        ),
        size: 150,
        Cell: ({ row, renderedCellValue }: any) => (
          <GoalsCell row={row} renderedCellValue={renderedCellValue} />
        ),
      },
      {
        accessorKey: "may",
        header: `May - ${selectedDateFilter?.label || currentYear}`,
        Header: ({ row }: any) => (
          <GoalsHeader
            headerName={`May - ${selectedDateFilter?.label || currentYear}`}
            width={`150px`}
          />
        ),
        size: 150,
        Cell: ({ row, renderedCellValue }: any) => (
          <GoalsCell row={row} renderedCellValue={renderedCellValue} />
        ),
      },
      {
        accessorKey: "june",
        header: `Jun - ${selectedDateFilter?.label || currentYear}`,
        Header: ({ row }: any) => (
          <GoalsHeader
            headerName={`Jun - ${selectedDateFilter?.label || currentYear}`}
            width={`150px`}
          />
        ),
        size: 150,
        Cell: ({ row, renderedCellValue }: any) => (
          <GoalsCell row={row} renderedCellValue={renderedCellValue} />
        ),
      },
      {
        accessorKey: "july",
        header: `Jul - ${selectedDateFilter?.label || currentYear}`,
        Header: ({ row }: any) => (
          <GoalsHeader
            headerName={`Jul - ${selectedDateFilter?.label || currentYear}`}
            width={`150px`}
          />
        ),
        size: 150,
        Cell: ({ row, renderedCellValue }: any) => (
          <GoalsCell row={row} renderedCellValue={renderedCellValue} />
        ),
      },
      {
        accessorKey: "aug",
        header: `Aug - ${selectedDateFilter?.label || currentYear}`,
        Header: ({ row }: any) => (
          <GoalsHeader
            headerName={`Aug - ${selectedDateFilter?.label || currentYear}`}
            width={`150px`}
          />
        ),
        size: 150,
        Cell: ({ row, renderedCellValue }: any) => (
          <GoalsCell row={row} renderedCellValue={renderedCellValue} />
        ),
      },
      {
        accessorKey: "sep",
        header: `Sep - ${selectedDateFilter?.label || currentYear}`,
        Header: ({ row }: any) => (
          <GoalsHeader
            headerName={`Sep - ${selectedDateFilter?.label || currentYear}`}
            width={`150px`}
          />
        ),
        size: 150,
        Cell: ({ row, renderedCellValue }: any) => (
          <GoalsCell row={row} renderedCellValue={renderedCellValue} />
        ),
      },
      {
        accessorKey: "oct",
        header: `Oct - ${selectedDateFilter?.label || currentYear}`,
        Header: ({ row }: any) => (
          <GoalsHeader
            headerName={`Oct - ${selectedDateFilter?.label || currentYear}`}
            width={`150px`}
          />
        ),
        size: 150,
        Cell: ({ row, renderedCellValue }: any) => (
          <GoalsCell row={row} renderedCellValue={renderedCellValue} />
        ),
      },
      {
        accessorKey: "nov",
        header: `Nov - ${selectedDateFilter?.label || currentYear}`,
        Header: ({ row }: any) => (
          <GoalsHeader
            headerName={`Nov - ${selectedDateFilter?.label || currentYear}`}
            width={`150px`}
          />
        ),
        size: 150,
        Cell: ({ row, renderedCellValue }: any) => (
          <GoalsCell row={row} renderedCellValue={renderedCellValue} />
        ),
      },
      {
        accessorKey: "dec",
        header: `Dec - ${selectedDateFilter?.label || currentYear}`,
        Header: ({ row }: any) => (
          <GoalsHeader
            headerName={`Dec - ${selectedDateFilter?.label || currentYear}`}
            width={`150px`}
          />
        ),
        size: 150,
        Cell: ({ row, renderedCellValue }: any) => (
          <GoalsCell row={row} renderedCellValue={renderedCellValue} />
        ),
      },
    ],
    [selectedDateFilter, enableSelectiveEditing, goalsTableData],
  )

  const tableConfig: MRT_TableOptions<any> = useMemo(
    () => ({
      columns,
      data: goalsTableData,
      state: {},
      enableEditing: enableSelectiveEditing,
      editDisplayMode: "cell",
      enableColumnActions: false,
      enableColumnFilters: false,
      enablePagination: false,
      enableSorting: false,
      enableBottomToolbar: false,
      enableStickyHeader: true,
      enableTopToolbar: false,
      enablePinning: true,
      enableExpanding: true,
      initialState: {
        columnVisibility: { "mrt-row-expand": false },
        columnPinning: {
          left: [
            "mrt-row-expand",
            "expanded_row",
            "metric_name",
            "baseline_value",
            "metric_benchmark",
            "trend_value",
          ],
        },
      },
      getSubRows: (originalRow: any) => originalRow?.subRows,
      muiTableProps: {
        sx: {
          tableLayout: "fixed",
        },
      },
      muiTableContainerProps: {
        sx: {
          height: "72vh",
        },
      },
      muiTableBodyProps: {
        sx: {
          height: "100%",
        },
      },
      muiTableHeadRowProps: {
        sx: {
          height: "40px",
          textAlign: "center",
          alignItems: "center",
        },
      },
      muiTableHeadCellProps: {
        sx: {
          height: "40px",
          backgroundColor: colors.primaryOrange,
          border: "0.5px solid #E4E4E4",
          color: "#FFFFFF",
          textAlign: "center",
          padding: "0 !important",
          "& div": {
            height: "100%",
          },
        },
      },
      muiTableBodyRowProps: ({ row }: any) => {
        const enabled = enableSelectiveEditing(row)
        return {
          hover: false,
          sx: {
            backgroundColor: enabled ? "#fff" : colors.grey50,
            height: "64px",
            padding: "0 !important",
          },
        }
      },
      muiTableBodyCellProps: ({ table, cell }: any) => {
        return {
          onClick: () => {
            table.setEditingCell(cell)
          },
          sx: {
            border: "0.5px solid #E4E4E4",
            padding: "0 !important",

            "&:hover": {
              border: "0.5px solid #1B56CE",
            },
          },
        }
      },
      muiEditTextFieldProps: ({ row, cell }: any) => ({
        placeholder: cell.renderValue(),
        onBlur: (event: any) => {
          handleCellValueChange(cell, row, event.target.value)
        },
        autoFocus: true,
        type: "number",
        sx: {
          height: "32px",
          width: "100%",
          padding: "0px 10px",
          margin: "0",
        },
      }),
    }),
    [
      columns,
      loading,
      selectedDateFilter,
      enableSelectiveEditing,
      goalsTableData,
      selectedMetricGoalsTab?.metric_bucket,
    ],
  )

  return (
    <TableWrapper
      sx={{
        "& tr": {
          height: "54px",
        },
      }}
    >
      {Object.keys(selectedMetricGoalsTab || {}).length !== 0 && !loading ? (
        <MaterialReactTable {...tableConfig} />
      ) : (
        <NoBucketWrapper>
          {loading ? <CircularProgress /> : "Select A Bucket"}
        </NoBucketWrapper>
      )}
    </TableWrapper>
  )
}

export default MetricGoalsTable
